<template>
  <div v-if="toleranceStatus" class="rounded-lg p-4" :class="bannerClass">
    <div class="flex items-start">
      <font-awesome-icon :icon="icon" class="mr-3 mt-0.5" :class="iconClass" />
      <div class="flex-1">
        <h4 class="font-semibold" :class="textClass">{{ title }}</h4>
        <p class="text-sm mt-1" :class="descClass">{{ description }}</p>
        
        <!-- Section details -->
        <div v-if="toleranceStatus.sections && toleranceStatus.sections.length > 0" class="mt-3 space-y-2">
          <div
            v-for="section in toleranceStatus.sections"
            :key="section.tolerance"
            class="text-sm flex items-center justify-between bg-white/50 rounded px-3 py-2"
          >
            <span class="font-medium">{{ section.tolerance }} Tolerance Section</span>
            <span :class="getSectionStatusClass(section)">
              {{ formatCurrency(section.current_total) }} / {{ formatCurrency(section.disclosed_total) }}
              <span v-if="section.variance > 0" class="ml-1">
                (+{{ formatCurrency(section.variance) }})
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'ToleranceBanner',
  props: {
    toleranceStatus: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const status = computed(() => {
      if (!props.toleranceStatus) return 'ok'
      if (props.toleranceStatus.has_violation) return 'exceeded'
      if (props.toleranceStatus.has_warning) return 'warning'
      return 'ok'
    })

    const bannerClass = computed(() => {
      switch (status.value) {
        case 'exceeded':
          return 'tolerance-exceeded'
        case 'warning':
          return 'tolerance-warning'
        default:
          return 'tolerance-ok'
      }
    })

    const icon = computed(() => {
      switch (status.value) {
        case 'exceeded':
          return 'exclamation-circle'
        case 'warning':
          return 'exclamation-triangle'
        default:
          return 'check-circle'
      }
    })

    const iconClass = computed(() => {
      switch (status.value) {
        case 'exceeded':
          return 'text-danger-600'
        case 'warning':
          return 'text-warning-600'
        default:
          return 'text-success-600'
      }
    })

    const textClass = computed(() => {
      switch (status.value) {
        case 'exceeded':
          return 'text-danger-800'
        case 'warning':
          return 'text-warning-800'
        default:
          return 'text-success-800'
      }
    })

    const descClass = computed(() => {
      switch (status.value) {
        case 'exceeded':
          return 'text-danger-700'
        case 'warning':
          return 'text-warning-700'
        default:
          return 'text-success-700'
      }
    })

    const title = computed(() => {
      switch (status.value) {
        case 'exceeded':
          return 'Tolerance Exceeded'
        case 'warning':
          return 'Approaching Tolerance Limit'
        default:
          return 'Within Tolerance'
      }
    })

    const description = computed(() => {
      switch (status.value) {
        case 'exceeded':
          return 'Current fees exceed disclosed amounts beyond allowable tolerance. Document generation is blocked until fees are adjusted or a tolerance cure is applied.'
        case 'warning':
          return 'Current fees are approaching the tolerance limit. Review carefully before proceeding.'
        default:
          return 'All fee sections are within allowable tolerance limits.'
      }
    })

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '$0.00'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value)
    }

    const getSectionStatusClass = (section) => {
      if (section.is_exceeded) return 'text-danger-700 font-semibold'
      if (section.is_warning) return 'text-warning-700 font-semibold'
      return 'text-success-700'
    }

    return {
      status,
      bannerClass,
      icon,
      iconClass,
      textClass,
      descClass,
      title,
      description,
      formatCurrency,
      getSectionStatusClass,
    }
  },
}
</script>





